import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Users, Grid3x3, ChefHat } from 'lucide-react';
import { useContent } from '../../context/ContentContext';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';

const iconMap = {
  sparkles: Sparkles,
  users: Users,
  grid3x3: Grid3x3,
  chefHat: ChefHat
};

const serviceKeys = ['housekeeping', 'frontOffice', 'windowCleaning', 'kitchen'];

const VideoServiceSlide = ({ service, index }) => {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const Icon = iconMap[service.icon];
  const { t } = useLanguage();
  const serviceKey = serviceKeys[index];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
          if (videoRef.current) {
            if (entry.isIntersecting) {
              videoRef.current.play().catch(() => {
                // Ignore play() interruption errors - they occur during rapid scroll
              });
            } else {
              videoRef.current.pause();
            }
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, 1 - (rect.top / window.innerHeight)));
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const translatedTitle = t('title', translations.services[serviceKey]);
  const translatedDescription = t('description', translations.services[serviceKey]);
  const inquireText = t('inquire', { de: 'Anfragen', en: 'Inquire' });

  return (
    <section 
      ref={sectionRef} 
      className="relative h-screen overflow-hidden" 
      data-testid={`service-slide-${service.id}`}
    >
      {/* Video Background - Direct MP4 with lazy loading for better performance */}
      <div className="absolute inset-0 overflow-hidden bg-[#003366] flex flex-col">
        {/* HTML5 Video - 80% height on mobile starting from top, full on desktop */}
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-[80%] md:h-full object-cover"
        >
          <source src={service.videoUrl} type="video/mp4" />
        </video>
        
        {/* Desktop: Navy gradient overlay - Top (smooth fade) */}
        <div 
          className="hidden md:block absolute inset-x-0 top-0 h-[15%] pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom, rgba(0, 51, 102, 0.4) 0%, rgba(0, 51, 102, 0.3) 30%, rgba(0, 51, 102, 0.15) 60%, rgba(0, 51, 102, 0.05) 80%, transparent 100%)'
          }}
        />
        
        {/* Desktop: Navy gradient overlay - Bottom (80% solid, smooth fade) */}
        <div 
          className="hidden md:block absolute inset-x-0 bottom-0 h-[30%] pointer-events-none"
          style={{
            background: 'linear-gradient(to top, rgba(0, 51, 102, 0.8) 0%, rgba(0, 51, 102, 0.8) 30%, rgba(0, 51, 102, 0.6) 50%, rgba(0, 51, 102, 0.4) 65%, rgba(0, 51, 102, 0.2) 80%, transparent 100%)'
          }}
        />
        
        {/* Mobile: Navy gradient at bottom of video */}
        <div className="md:hidden absolute inset-x-0 top-[70%] h-[10%] bg-gradient-to-b from-transparent to-[#003366] pointer-events-none" />
      </div>

      {/* Mobile: All text content below video (bottom 20%) */}
      <div className="md:hidden absolute bottom-0 left-0 right-0 h-[20%] z-20 bg-[#003366] flex flex-col items-center justify-center px-4 py-2">
        <div className="flex items-center justify-center gap-2 mb-1">
          <div className="w-6 h-6 rounded-lg bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
            {Icon && <Icon className="w-3 h-3 text-[#003366]" />}
          </div>
          <h2 className="text-sm font-semibold text-white leading-tight text-center">
            {translatedTitle}
          </h2>
        </div>
        <p className="text-white/90 text-xs leading-relaxed mb-2 text-center max-w-[95%]">
          {translatedDescription}
        </p>
        <Link
          to="/contact"
          className="group inline-flex items-center gap-2 px-3 py-1.5 bg-[#D4AF37] text-[#003366] font-semibold text-xs rounded-md border border-[#B8960C] hover:bg-[#C9A227] transition-all duration-300"
          data-testid={`service-cta-mobile-${service.id}`}
        >
          <span>{inquireText}</span>
          <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>

      {/* Desktop: Lower Third Content */}
      <div className="hidden md:block absolute bottom-0 left-0 right-0 z-10 pb-10 md:pb-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div 
            className="relative flex items-center justify-between"
            style={{ 
              opacity: isVisible ? 1 : 0, 
              transform: `translateY(${isVisible ? 0 : 30}px)`,
              transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s'
            }}
          >
            {/* Golden accent line */}
            <div 
              className="absolute -left-4 top-0 bottom-0 w-1 bg-[#D4AF37]"
              style={{
                transform: `scaleY(${isVisible ? 1 : 0})`,
                transformOrigin: 'top',
                transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.3s'
              }}
            />
            
            {/* Left Content: Title + Description */}
            <div className="flex-1 max-w-2xl">
              {/* Title Row */}
              <div 
                className="flex items-center gap-4 mb-3"
                style={{ 
                  opacity: isVisible ? 1 : 0, 
                  transform: `translateX(${isVisible ? 0 : -20}px)`,
                  transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.4s'
                }}
              >
                <div className="w-8 h-8 rounded-lg bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                  {Icon && <Icon className="w-4 h-4 text-[#003366]" />}
                </div>
                <h2 className="text-xl lg:text-2xl font-semibold text-white leading-[1.2] drop-shadow-lg">
                  {translatedTitle}
                </h2>
              </div>

              {/* Description */}
              <p 
                className="text-white text-sm md:text-base leading-[1.8] text-justify drop-shadow-lg"
                style={{ 
                  opacity: isVisible ? 1 : 0, 
                  transform: `translateY(${isVisible ? 0 : 15}px)`,
                  transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.5s'
                }}
              >
                {translatedDescription}
              </p>
            </div>

            {/* Right: Button vertically centered */}
            <div 
              className="flex items-center ml-8"
              style={{ 
                opacity: isVisible ? 1 : 0, 
                transform: `translateX(${isVisible ? 0 : 20}px)`,
                transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.5s'
              }}
            >
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#003366] font-semibold text-sm rounded-md border border-[#B8960C] hover:bg-[#C9A227] hover:scale-105 transition-all duration-300 flex-shrink-0"
                data-testid={`service-cta-${service.id}`}
              >
                <span>{inquireText}</span>
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const VideoServicesSection = () => {
  const { services } = useContent();
  
  return (
    <div 
      id="services" 
      className="relative"
    >
      {/* Video Service Slides */}
      {services.map((service, index) => (
        <div key={service.id}>
          <VideoServiceSlide service={service} index={index} />
          {/* Animated Golden Glow Line Between All Slides */}
          <div className="relative h-[2px] bg-black overflow-hidden">
            <div 
              className="absolute top-0 h-full w-[300px]"
              style={{
                background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                animation: 'serviceGlowLine 4s ease-in-out infinite',
                boxShadow: '0 0 20px #D4AF37, 0 0 40px #D4AF37',
              }}
            />
          </div>
        </div>
      ))}
      
      {/* Glow Line Animation - Now in index.css */}
    </div>
  );
};

export default VideoServicesSection;
